import type { CaptureResult } from 'posthog-js'
import { parseUrl, originPathname } from '@lib/posthog-url-sanitize'

export type LcpElementMeta = {
  tag: string
  id: string | null
  src: string | null
  alt: string | null
  selector: string | null
}

/** An element observed as the LCP, tied to the page it was observed on. */
export type LcpCapture = {
  url: string
  meta: LcpElementMeta
}

type LcpPerformanceEntry = PerformanceEntry & { element?: Element }

const MAX_SRC_LENGTH = 200
const MAX_ALT_LENGTH = 100
const MAX_SELECTOR_LENGTH = 120
const MAX_SELECTOR_CLASSES = 3

function truncate(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value
}

// An image URL's query string is where user-identifying data lives — Cloudinary
// signatures, transform params, cache-busting ids — and event properties must
// never carry raw PII. Origin + pathname still says which asset was the LCP
// element, which is all the insight needs.
function sanitizeSrc(raw: string): string {
  const url = parseUrl(raw, typeof location === 'undefined' ? undefined : location.href)
  if (!url) return truncate(raw, MAX_SRC_LENGTH)
  // A data: URI's pathname is the entire base64 payload. Never send that.
  return url.protocol === 'data:' ? 'data:' : originPathname(url)
}

// Tailwind class attributes run to hundreds of characters, which makes the
// property useless as a breakdown key. Keep enough to identify the element.
function sanitizeSelector(className: string): string | null {
  const classes = className.trim().split(/\s+/).filter(Boolean)
  if (classes.length === 0) return null
  return truncate('.' + classes.slice(0, MAX_SELECTOR_CLASSES).join('.'), MAX_SELECTOR_LENGTH)
}

// PerformanceEntry.element is a live DOM node reference that PostHog's
// $web_vitals event can't JSON-serialize (it becomes {}). Extract the plain
// fields we need while the reference is still alive.
export function extractLcpElementMeta(element: Element): LcpElementMeta {
  const className = element instanceof HTMLElement ? element.className : ''
  const image = element as HTMLImageElement
  const src = image.src || image.currentSrc || ''
  const alt = image.alt || ''
  return {
    tag: element.tagName.toLowerCase(),
    id: element.id || null,
    src: src ? sanitizeSrc(src) : null,
    alt: alt ? truncate(alt, MAX_ALT_LENGTH) : null,
    selector: sanitizeSelector(className),
  }
}

export function latestLcpCapture(entries: LcpPerformanceEntry[], url: string): LcpCapture | null {
  const last = entries[entries.length - 1]
  return last?.element ? { url, meta: extractLcpElementMeta(last.element) } : null
}

/**
 * The page a $web_vitals event describes. posthog-js buffers metrics per URL
 * and stamps each one with the URL it was measured on, then flushes the buffer
 * when a metric for a *different* URL arrives — by which point the event-level
 * `$current_url` is already the next page. The metric's own `$current_url` is
 * therefore the authoritative one; the event-level value is only a fallback.
 */
function lcpEventUrl(event: CaptureResult): string | null {
  const metric = event.properties['$web_vitals_LCP_event'] as { $current_url?: unknown } | undefined
  if (typeof metric?.$current_url === 'string') return metric.$current_url

  const eventUrl = event.properties['$current_url']
  return typeof eventUrl === 'string' ? eventUrl : null
}

/** Same document ignoring the fragment — an in-page anchor is not a new page. */
function sameDocument(a: string, b: string): boolean {
  return a.split('#')[0] === b.split('#')[0]
}

export function applyLcpElementMeta(event: CaptureResult, capture: LcpCapture | null): CaptureResult {
  if (event.event !== '$web_vitals') return event

  // The observer holds the last LCP element for the lifetime of the JS context,
  // which outlives a soft navigation. Only attach it to an event measured on
  // the same page. A URL we cannot read is not evidence of a mismatch, so the
  // capture still applies then.
  const eventUrl = lcpEventUrl(event)
  const meta = capture && (eventUrl === null || sameDocument(eventUrl, capture.url)) ? capture.meta : null

  // Stamped on every $web_vitals event, so a browser with no LCP element to
  // report (LargestContentfulPaint.element is Chromium-only) is
  // distinguishable in PostHog from a build that predates this code, where the
  // property is absent entirely. Without it the two look identical.
  event.properties['lcp_element_captured'] = meta !== null

  if (meta) {
    event.properties['lcp_element_tag'] = meta.tag
    event.properties['lcp_element_id'] = meta.id
    event.properties['lcp_element_src'] = meta.src
    event.properties['lcp_element_alt'] = meta.alt
    event.properties['lcp_element_selector'] = meta.selector
  }

  return event
}
